/**
 * dscMatchingService.js
 *
 * Pure-JS port of the 8 `@AuraEnabled` methods in
 * `MRT upgrade/My%20Rock%20Tools/force-app/main/default/classes/DSCController.cls`
 * (342 lines, 1-258 are the methods, 260-341 the FilterWrapper class).
 *
 * Each Apex method = one named export. The Apex code queried `Product2`
 * directly; this JS port takes a pre-loaded `products` array (from
 * `products-tophammer.json` / `products-dth.json`) and applies the same
 * string filters in memory. The shape of the returned objects mirrors the
 * Apex `FilterWrapper` shape — camelCase field names verbatim from
 * `DSCController.cls:260-302`.
 *
 * Source-of-truth quotes:
 *  - getFilteringData                              DSCController.cls:2-83
 *  - getDTHHammerData                              DSCController.cls:85-97
 *  - getDTHHammerDataOptions                       DSCController.cls:99-117
 *  - getDTHDrillData                               DSCController.cls:119-131
 *  - getThreadInformation                          DSCController.cls:133-152
 *  - getThreadF1ValuesBasedOnThreadF2AndCouplingType  DSCController.cls:153-179
 *  - getCouplingThreadforRods                      DSCController.cls:180-216
 *  - getCouplingThreadforBits                      DSCController.cls:218-237
 *  - getCouplingButtonShape                        DSCController.cls:239-258
 *
 * PWA-vs-Apex field mapping notes:
 *  - `Item_Group_Major__c` (Apex string '184') → JSON `pgmj` (NUMBER 184).
 *  - `Item_Group_Minor__c`  → JSON `pgmn` (string).
 *  - `TH_Rock_Drill_Manufacturer__c` → JSON `rockDrillManufacturer`.
 *  - `TH_Compatible_Rock_Drill__c`   → JSON `compatibleRockDrill`.
 *    Semicolon-delimited (DSCController.cls:21).
 *  - `Thread__c`            → JSON `thread`.
 *  - `Button_Shape__c`      → JSON `buttonShape`.
 *  - `Reduction_Coupling__c`→ JSON `reductionCoupling` ('Normal' /
 *    'Reduction Coupling' — searchResults.js:1183-1184).
 *  - `Product_Display__c` + `PICS__c IN ('S','R','N')` → handled by
 *    inline helpers below. The full `./dataService` helpers are NOT imported
 *    at module top because the Linux test mount sometimes sees a truncated
 *    copy of dataService.js (OneDrive bidirectional truncation issue
 *    documented in STATUS-2026-05-17-EOD.md "Rule 3"). SP2/SP3 can re-wire
 *    per-entity availability once that is mitigated.
 *
 * @module services/dscMatchingService
 * @since 2026-05-20 (Group E SP1.2)
 */

// ============================================================================
// Inline display-gate helpers — guest fallback.
// ============================================================================

function _getEffectivePics(product) {
  return product?.pics ?? null;
}
function _isDisplayable(/* itemCode, entityCode */) {
  // Without a per-entity availability record we cannot determine display.
  // Matches the guest fallback in dataService.filterByPicsView.
  return true;
}

function pgmjEquals(product, expected) {
  if (product == null) return false;
  return String(product.pgmj ?? product.itemGroupMajor ?? '') === String(expected);
}

function pgmnEquals(product, expected) {
  if (product == null) return false;
  return String(product.pgmn ?? product.itemGroupMinor ?? '') === String(expected);
}

function passesLegacyDisplayGate(product, entityCode) {
  if (!product) return false;
  if (entityCode) {
    if (!_isDisplayable(product.itemCode, entityCode)) return false;
  }
  const pics = entityCode ? _getEffectivePics(product) : product?.pics;
  return pics === 'S' || pics === 'R' || pics === 'N';
}

function splitSemicolon(val) {
  if (val == null) return [];
  return String(val)
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Shank business-logic block — PORT VERBATIM of DSCController.cls:56-69.
 */
export function deriveSelectedShank(compatibleShank) {
  if (compatibleShank == null) return '';
  const s = String(compatibleShank);
  if (s === '560-4') return 'QLX560';
  if (s === '650-6') return 'S';
  if (s.includes('M')) return 'MISSION';
  if (s.includes('QL')) return 'QLX';
  if (s.includes('DHD')) return 'DHD';
  if (s.includes('560')) return '560';
  return '';
}

// ============================================================================
// 1. getFilteringData(type, products)            — DSCController.cls:2-83
// ============================================================================

export function getFilteringData(type, products, opts = {}) {
  const { entityCode = null } = opts;
  const result = [];
  if (!Array.isArray(products)) return result;

  if (type === 'top-hammer') {
    const threadToButtonShapeMap = new Map();
    for (const p of products) {
      if (p?.thread && p?.buttonShape) {
        if (!threadToButtonShapeMap.has(p.thread)) {
          threadToButtonShapeMap.set(p.thread, new Set());
        }
        threadToButtonShapeMap.get(p.thread).add(p.buttonShape);
      }
    }
    const keys = new Set();
    for (const p of products) {
      if (
        !p?.rockDrillManufacturer ||
        !p?.compatibleRockDrill ||
        !p?.thread ||
        !p?.itemCode
      ) continue;
      const buttonShapes = threadToButtonShapeMap.get(p.thread);
      if (!buttonShapes) continue;
      for (const compatibleRockDrill of splitSemicolon(p.compatibleRockDrill)) {
        for (const buttonShape of buttonShapes) {
          const key = String(p.rockDrillManufacturer) + String(compatibleRockDrill) + String(p.thread) + String(buttonShape);
          if (keys.has(key)) continue;
          keys.add(key);
          result.push({
            manufacture: p.rockDrillManufacturer,
            compatibleRockDrill,
            itemCode: p.itemCode,
            thread: p.thread,
            buttonShape,
          });
        }
      }
    }
    return result;
  }

  // DTH branch (lines 33-79).
  // shankTypeToPin is sourced from HAMMERS — restrict to in-scope hammer
  // families so RH510/RH460 shank types don't leak into bit derivation
  // (business rule 20 May 2026 — see DTH_DSC_IN_SCOPE_FAMILIES).
  const shankTypeToPin = new Map();
  for (const p of products) {
    if (!p?.compatibleShankType || !p?.topsubPinOption) continue;
    if (p?.hammerFamily && !DTH_DSC_IN_SCOPE_FAMILIES.includes(p.hammerFamily)) continue;
    if (!shankTypeToPin.has(p.compatibleShankType)) {
      shankTypeToPin.set(p.compatibleShankType, new Set());
    }
    shankTypeToPin.get(p.compatibleShankType).add(p.topsubPinOption);
  }
  const keys = new Set();
  for (const p of products) {
    if (!pgmjEquals(p, 832)) continue;
    if (entityCode && !_isDisplayable(p.itemCode, entityCode)) continue;
    if (!p?.bitDiameter_in) continue;
    if (!p?.compatibleShankType) continue;
    const minor = String(p.pgmn ?? p.itemGroupMinor ?? '');
    if (minor !== 'D04' && minor !== 'D14') continue;
    const pinSet = shankTypeToPin.get(p.compatibleShankType);
    if (!pinSet) continue;
    for (const compatibleShank of splitSemicolon(p.compatibleShankType)) {
      for (const pin of pinSet) {
        const key = String(p.bitDiameter_in) + String(compatibleShank) + String(pin);
        if (keys.has(key)) continue;
        keys.add(key);
        result.push({
          headDiameter: p.bitHeadDiameter_in ?? null,
          shankType: compatibleShank,
          pin,
          bitDiameter: p.bitDiameter_in,
          bitDiameterMM: p.bitDiameter_mm ?? null,
          bitDiameterInch: p.bitDiameter_in,
          shank: deriveSelectedShank(compatibleShank),
          // DTH JSON uses `shankSize_in` (snake_camel hybrid); legacy Apex
          // field was Shank_Size__c. Keep both fallbacks defensively.
          shankSize: p.shankSize_in ?? p.shankSize ?? null,
          bitShankType: p.shankType ?? null,
          hammerFamily: p.hammerFamily ?? null,
        });
      }
    }
  }
  return result;
}

// ============================================================================
// 2. getDTHHammerData(products)                  — DSCController.cls:85-97
// ============================================================================

/**
 * In-scope DTH hammer families for the MRT DSC. Vandana (20 May walkthrough):
 * "we are to exclude the RH510 and RH460 hammers from the DTH DSC flow — the
 * matching logic for those families is different from what's provided." Only
 * RH560 and RH570 are supported by the current cascade rules.
 */
export const DTH_DSC_IN_SCOPE_FAMILIES = ['RH560', 'RH570'];

export function getDTHHammerData(products, opts = {}) {
  const { entityCode = null } = opts;
  const result = [];
  if (!Array.isArray(products)) return result;
  for (const p of products) {
    if (!pgmjEquals(p, 830)) continue;
    if (entityCode && !_isDisplayable(p.itemCode, entityCode)) continue;
    if (!p?.topsubPinOption) continue;
    if (!p?.compatibleShankType) continue;
    // Filter swap (20 May 2026): legacy Apex used `Item_Group_Minor__c IN
    // ('D45','D55')` but PWA hammers carry a wide range of itemGroupMinor
    // codes (D41-D90, MRT23-MRT50, etc.) — D45/D55 catches only 21 of 630.
    // The right discriminator for the MRT DSC is `hammerFamily IN
    // (RH560, RH570)` — aligns with the data AND enforces the business rule.
    if (!p?.hammerFamily || !DTH_DSC_IN_SCOPE_FAMILIES.includes(p.hammerFamily)) continue;
    if (!p?.hammerSize_in) continue;
    result.push({
      shankType: p.compatibleShankType,
      pin: p.topsubPinOption,
      shank: p.shank ?? null,
      dthHammerOutsideDiameterMM: p.hammerOutsideDiameter_mm ?? null,
      diameterMM: p.diameter_mm ?? null,
      productDisplay: true,
      hammerFamily: p.hammerFamily ?? null,
      hammerSizeInch: p.hammerSize_in,
      shankTypeC: p.shankType ?? null,
    });
  }
  return result;
}

// ============================================================================
// 3. getDTHHammerDataOptions(products, entityCode)  — DSCController.cls:99-117
// ============================================================================

export function getDTHHammerDataOptions(products, entityCode) {
  const result = [];
  if (!Array.isArray(products)) return result;
  const effectiveEntity = entityCode || '4782';
  for (const p of products) {
    const isHammerByDesc = String(p?.pgmjDesc || '').toLowerCase() === 'dth hammers';
    if (!isHammerByDesc && !pgmjEquals(p, 830)) continue;
    if (!p?.topsubPinOption) continue;
    if (!p?.hammerFamily) continue;
    if (p.hammerFamily === 'ROTO Unclassified') continue;
    // Business-rule scope (20 May 2026): MRT DSC supports RH560 + RH570 only.
    if (!DTH_DSC_IN_SCOPE_FAMILIES.includes(p.hammerFamily)) continue;
    if (!p?.hammerSize_in) continue;
    if (!_isDisplayable(p.itemCode, effectiveEntity)) continue;
    result.push({
      shankType: p.compatibleShankType ?? null,
      pin: p.topsubPinOption,
      shank: p.shank ?? null,
      dthHammerOutsideDiameterMM: p.hammerOutsideDiameter_mm ?? null,
      diameterMM: p.diameter_mm ?? null,
      productDisplay: true,
      hammerFamily: p.hammerFamily,
      hammerSizeInch: p.hammerSize_in,
      shankTypeC: p.shankType ?? null,
    });
  }
  return result;
}

// ============================================================================
// 4. getDTHDrillData(products)                   — DSCController.cls:119-131
// ============================================================================

export function getDTHDrillData(products, opts = {}) {
  const { entityCode = null } = opts;
  const result = [];
  if (!Array.isArray(products)) return result;
  for (const p of products) {
    if (!pgmjEquals(p, 849)) continue;
    if (entityCode && !_isDisplayable(p.itemCode, entityCode)) continue;
    result.push({
      pin: p.topsubPinOption ?? null,
      diameterMM: p.diameter_mm ?? null,
    });
  }
  return result;
}

// ============================================================================
// 5. getThreadInformation                       — DSCController.cls:133-152
// ============================================================================

export function getThreadInformation(brandName, model, products, itemGroupMajor, itemGroupMinor) {
  const out = [];
  const seen = new Set();
  if (!Array.isArray(products)) return out;
  for (const p of products) {
    if (!pgmjEquals(p, itemGroupMajor)) continue;
    if (!pgmnEquals(p, itemGroupMinor)) continue;
    if (p?.rockDrillManufacturer !== brandName) continue;
    if (!splitSemicolon(p?.compatibleRockDrill).includes(model)) continue;
    if (p?.thread == null) continue;
    if (seen.has(p.thread)) continue;
    seen.add(p.thread);
    out.push(p.thread);
  }
  return out;
}

// ============================================================================
// 6. getThreadF1ValuesBasedOnThreadF2AndCouplingType — DSCController.cls:153-179
// ============================================================================

export function getThreadF1ValuesBasedOnThreadF2AndCouplingType(
  couplingType,
  threadF2,
  products,
  itemGroupMajor,
  itemGroupMinor,
  opts = {}
) {
  const { entityCode = null } = opts;
  const out = [];
  const seen = new Set();
  if (!Array.isArray(products)) return out;
  for (const p of products) {
    if (!pgmjEquals(p, itemGroupMajor)) continue;
    if (!pgmnEquals(p, itemGroupMinor)) continue;
    if (!passesLegacyDisplayGate(p, entityCode)) continue;
    if (p?.threadF2 !== threadF2) continue;
    if (p?.reductionCoupling !== couplingType) continue;
    if (p?.threadF1 == null) continue;
    if (seen.has(p.threadF1)) continue;
    seen.add(p.threadF1);
    out.push(p.threadF1);
  }
  return out;
}

// ============================================================================
// 7. getCouplingThreadforRods                   — DSCController.cls:180-216
// ============================================================================

export function getCouplingThreadforRods(
  brandName,
  model,
  couplingType,
  products,
  itemGroupMajor,
  itemGroupMinor,
  opts = {}
) {
  const { entityCode = null } = opts;
  const thThread = getThreadInformation(brandName, model, products, 184, 'T20');
  const thThreadF2 = [];
  let thThread1 = [];

  if (thThread.length > 0) {
    const seenF2 = new Set();
    for (const p of products) {
      if (!pgmjEquals(p, itemGroupMajor)) continue;
      if (!pgmnEquals(p, itemGroupMinor)) continue;
      if (!passesLegacyDisplayGate(p, entityCode)) continue;
      if (p?.reductionCoupling !== couplingType) continue;
      if (!p?.threadF1 || !thThread.includes(p.threadF1)) continue;
      if (p?.threadF2 == null) continue;
      if (seenF2.has(p.threadF2)) continue;
      seenF2.add(p.threadF2);
      thThreadF2.push(p.threadF2);
    }
    if (thThreadF2.length > 0) {
      thThread1 = getCouplingThreadforBits(thThreadF2, products, 182, { entityCode });
    }
  }

  return { thThread, thThreadF2, thThread1 };
}

// ============================================================================
// 8. getCouplingThreadforBits                   — DSCController.cls:218-237
// ============================================================================

export function getCouplingThreadforBits(rodThread2, products, itemGroupMajor, opts = {}) {
  const { entityCode = null } = opts;
  const out = [];
  const seen = new Set();
  if (!Array.isArray(products) || !Array.isArray(rodThread2)) return out;
  for (const p of products) {
    if (!pgmjEquals(p, itemGroupMajor)) continue;
    if (!passesLegacyDisplayGate(p, entityCode)) continue;
    if (!p?.thread1 || !rodThread2.includes(p.thread1)) continue;
    if (p?.thread2 == null) continue;
    if (seen.has(p.thread2)) continue;
    seen.add(p.thread2);
    out.push(p.thread2);
  }
  return out;
}

// ============================================================================
// 9. getCouplingButtonShape                     — DSCController.cls:239-258
// ============================================================================

export function getCouplingButtonShape(thread, products, itemGroupMajor, opts = {}) {
  const { entityCode = null } = opts;
  const out = [];
  const seen = new Set();
  if (!Array.isArray(products) || !Array.isArray(thread)) return out;
  for (const p of products) {
    if (!pgmjEquals(p, itemGroupMajor)) continue;
    if (!passesLegacyDisplayGate(p, entityCode)) continue;
    if (!p?.thread || !thread.includes(p.thread)) continue;
    if (p?.buttonShape == null) continue;
    if (seen.has(p.buttonShape)) continue;
    seen.add(p.buttonShape);
    out.push(p.buttonShape);
  }
  return out;
}
